import { CheckCircleFilled } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { GalaTemplate, GalaTicketRow, TICKET_STATUS_META, formatVn } from '../contract';
import { fileUrl } from '../lib/file-url';
import { seatLabel } from '../lib/seat-text';
import styles from '../styles.module.scss';
import { ticketThumbKey } from './ticket-modals';

interface Props {
  rows: GalaTicketRow[];
  loading: boolean;
  templates: GalaTemplate[];
  page: number;
  limit: number;
  total: number;
  selectedIds: number[];
  onSelect: (ids: number[]) => void;
  onPage: (page: number, limit: number) => void;
  onEdit: (t: GalaTicketRow) => void;
  onLink: (t: GalaTicketRow) => void;
  onView: (t: GalaTicketRow) => void;
  onRevoke: (t: GalaTicketRow) => void;
  onRestore: (t: GalaTicketRow) => void;
  onDelete: (t: GalaTicketRow) => void;
}

/** Bảng vé mời (SPEC mục 8): ảnh vé, tên, @username, UID, nhóm, ghi chú, mẫu, trạng thái, check-in, thao tác. */
const TicketsTable = ({
  rows,
  loading,
  templates,
  page,
  limit,
  total,
  selectedIds,
  onSelect,
  onPage,
  onEdit,
  onLink,
  onView,
  onRevoke,
  onRestore,
  onDelete,
}: Props) => {
  const templateName = (t: GalaTicketRow) => {
    if (t.customImageKey) return 'Ảnh làm sẵn';
    if (!t.templateId) return 'Mặc định';
    return templates.find((x) => x.id === t.templateId)?.name ?? `#${t.templateId}`;
  };

  const columns: ColumnsType<GalaTicketRow> = [
    {
      title: 'Vé',
      key: 'thumb',
      width: 64,
      render: (_, t) => {
        const key = ticketThumbKey(t);
        return key ? (
          <button
            type="button"
            onClick={() => onView(t)}
            style={{ border: 0, padding: 0, background: 'none', cursor: 'zoom-in' }}
            aria-label={`Xem vé của ${t.displayName || t.shortCode}`}
          >
            <img className={styles.ticketThumb} src={fileUrl(key)} alt="" loading="lazy" />
          </button>
        ) : (
          <span className={styles.subtle}>—</span>
        );
      },
    },
    {
      title: 'Tên trên vé',
      dataIndex: 'displayName',
      render: (v: string, t) => (
        <span>
          {v || <span className={styles.subtle}>(trống)</span>}
          <div className={styles.subtle}>Mã {t.shortCode}</div>
        </span>
      ),
    },
    {
      title: 'Tài khoản',
      key: 'user',
      render: (_, t) =>
        t.user ? (
          <span>@{t.user.username}</span>
        ) : (
          <span className={styles.unlinked}>— chưa gắn —</span>
        ),
    },
    {
      title: 'UID TikTok',
      dataIndex: 'tiktokUid',
      render: (v: string | null) => v ?? <span className={styles.subtle}>—</span>,
    },
    {
      title: 'Nhóm',
      dataIndex: 'guestGroup',
      width: 90,
      render: (v: string | null) => (v ? <Tag>{v}</Tag> : <span className={styles.subtle}>—</span>),
    },
    {
      title: 'Ghế',
      key: 'seat',
      width: 140,
      render: (_, t) =>
        t.seat ? (
          <span className={styles.nowrap}>{seatLabel(t.seat.tableName, t.seat.number)}</span>
        ) : (
          <span className={styles.subtle}>—</span>
        ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      ellipsis: true,
      render: (v: string | null) => v ?? <span className={styles.subtle}>—</span>,
    },
    { title: 'Mẫu', key: 'template', width: 120, render: (_, t) => templateName(t) },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 150,
      render: (_, t) => {
        const meta = TICKET_STATUS_META[t.status];
        return (
          <Space size={4} wrap>
            <Tooltip title={t.status === 'REVOKED' ? t.revokeReason : undefined}>
              <Tag color={meta?.color}>{meta?.text ?? t.status}</Tag>
            </Tooltip>
            {t.renderError && (
              <Tooltip title={t.renderError}>
                <Tag color="gold">Cảnh báo vẽ</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Check-in',
      key: 'checkin',
      width: 80,
      align: 'center',
      render: (_, t) =>
        t.checkedInAt ? (
          <Tooltip title={`Đã check-in lúc ${formatVn(t.checkedInAt)}`}>
            <span>
              <CheckCircleFilled style={{ color: '#52c41a' }} aria-label="Đã check-in" />
              <span className={styles.subtle} style={{ display: "block" }}>
                {formatVn(t.checkedInAt).slice(-5)}
              </span>
            </span>
          </Tooltip>
        ) : (
          <span className={styles.subtle}>—</span>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right',
      width: 300,
      render: (_, t) => (
        <Space size={0} wrap>
          <Button type="link" size="small" onClick={() => onEdit(t)}>
            Sửa
          </Button>
          <Button type="link" size="small" onClick={() => onLink(t)}>
            {t.user ? 'Đổi tài khoản' : 'Gắn tài khoản'}
          </Button>
          <Button type="link" size="small" onClick={() => onView(t)}>
            Xem vé
          </Button>
          {t.status === 'REVOKED' ? (
            <Button type="link" size="small" onClick={() => onRestore(t)}>
              Khôi phục
            </Button>
          ) : (
            <Button type="link" size="small" danger onClick={() => onRevoke(t)}>
              Thu hồi
            </Button>
          )}
          <Popconfirm
            title="Xoá vé này?"
            description="Không xoá được vé đã check-in."
            okText="Xoá"
            cancelText="Huỷ"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(t)}
          >
            <Button type="link" size="small" danger>
              Xoá
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table<GalaTicketRow>
      rowKey="id"
      size="small"
      loading={loading}
      columns={columns}
      dataSource={rows}
      scroll={{ x: 1200 }}
      rowSelection={{
        selectedRowKeys: selectedIds,
        onChange: (keys) => onSelect(keys.map(Number)),
        preserveSelectedRowKeys: true,
      }}
      pagination={{
        current: page,
        pageSize: limit,
        total,
        showSizeChanger: true,
        pageSizeOptions: [20, 50, 100, 200],
        showTotal: (n) => `${n} vé`,
        onChange: onPage,
      }}
      locale={{ emptyText: 'Chưa có khách mời nào. Bấm "Thêm khách mời".' }}
    />
  );
};

export default TicketsTable;
